import { ChakraProvider, Box } from '@chakra-ui/react'
import { useState, useEffect } from 'react'
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import { MatchSetup } from '@/components/MatchSetup'
import { JoinMatch } from '@/components/JoinMatch'
import { ScoringScreen } from '@/components/ScoringScreen'
import { TennisScoringScreen } from '@/components/TennisScoringScreen'
import { LandingPage } from '@/components/LandingPage'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { BrowserCompatibilityWarning } from '@/components/common/BrowserCompatibilityWarning'
import { MatchData } from '@/types/match'
import { getMatch } from '@/services/firebase'
import Pricing from '@/components/Pricing'
import { AdminPage } from '@/components/AdminPage'
import { AuthProvider } from '@/contexts/AuthContext'
import { PaywallProvider } from '@/contexts/PaywallContext'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { AuthPage } from '@/components/auth/AuthPage'
import { ProfilePage } from '@/components/auth/ProfilePage'
import { PaywallGuard } from '@/components/PaywallGuard'
import { EmailVerificationGuard } from '@/components/auth/EmailVerificationGuard'
import { useAuthContext } from '@/contexts/AuthContext'
import { 
  generateNewControllerToken, 
  validateControllerToken 
} from '@/utils/controllerTokenManager'
import VerifyCode from './components/auth/VerifyCode';
import Terms from './components/Terms';
import { PaywallPage } from './components/PaywallPage';
import { ResetPassword } from './components/auth/ResetPassword';
import { ScoringAccessGuard } from './components/ScoringAccessGuard';
import { TrialBlockingGuard } from './components/TrialBlockingGuard';
import { MatchLoader } from './components/MatchLoader';
import { PaymentAlertToast } from './components/PaymentAlertToast';

// Main app content component
function AppContent() {
  const navigate = useNavigate()
  const location = useLocation()
  const [currentScreen, setCurrentScreen] = useState<'landing' | 'setup' | 'join' | 'scoring' | 'tennis'>('landing')
  const [matchData, setMatchData] = useState<MatchData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isController, setIsController] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Check URL parameters on mount
  useEffect(() => {
    setError(null) // Reset error on navigation
    const params = new URLSearchParams(location.search)
    const matchId = params.get('matchId')
    const token = params.get('token')
    const sport = params.get('sport')
    const screen = params.get('screen')

    // If no matchId and no specific screen requested, show landing page
    if (!matchId && !screen) {
      setCurrentScreen('landing')
      return
    }

    // If screen parameter is 'join', show join screen
    if (screen === 'join') {
      setCurrentScreen('join')
      return
    }

    if (matchId) {
      setIsLoading(true)
      getMatch(matchId)
        .then(async data => {
          if (data) {
            console.log('Match data received:', data)
            setMatchData(data)
            
            // Handle controller token logic
            if (token) {
              try {
                const validation = await validateControllerToken(matchId, token)
                if (!validation.isValid) {
                  const newToken = await generateNewControllerToken(matchId)
                  const shouldShowTennis = data.scoringStructure === 'tennis'
                  const newUrl = `/${shouldShowTennis ? 'tennis' : 'scoring'}?matchId=${matchId}&token=${newToken}&controller=true`
                  navigate(newUrl, { replace: true })
                  return
                }
                setIsController(true)
              } catch (error: any) {
                setError('There was a problem connecting as a controller. Please try again or refresh the page.')
                setIsController(false)
                return
              }
            } else {
              setIsController(false)
            }
            
            // Check scoring structure to determine which screen to show
            const shouldShowTennis = data.scoringStructure === 'tennis'
            console.log('Should show tennis screen:', shouldShowTennis)
            setCurrentScreen(shouldShowTennis ? 'tennis' : 'scoring')
            // Update URL to reflect the correct sport
            if (shouldShowTennis && !sport) {
              navigate(`/tennis?matchId=${matchId}${token ? `&token=${token}` : ''}`, { replace: true })
            }
          } else {
            setCurrentScreen('join')
          }
        })
        .catch(error => {
          setError('Could not connect to the match. Please check your connection or try again later.')
          setCurrentScreen('join')
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [location.search, navigate])

  const handleLandingGetStarted = () => {
    setCurrentScreen('setup')
  }

  const handleBackToLanding = () => {
    setCurrentScreen('landing')
  }

  const handleSetupComplete = (data: MatchData) => {
    console.log('Setup complete with data:', data)
    setMatchData(data)
    const isTennis = data.scoringStructure === 'tennis'
    console.log('Is tennis match:', isTennis)
    setCurrentScreen(isTennis ? 'tennis' : 'scoring')
    navigate(`/${isTennis ? 'tennis' : 'scoring'}?matchId=${data.id}&token=${data.controllerToken}`)
  }

  const handleJoinMatch = (data: MatchData) => {
    console.log('Joining match with data:', data)
    setMatchData(data)
    const isTennis = data.scoringStructure === 'tennis'
    console.log('Is tennis match:', isTennis)
    setCurrentScreen(isTennis ? 'tennis' : 'scoring')
    navigate(`/${isTennis ? 'tennis' : 'scoring'}?matchId=${data.id}`)
  }

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <Box minH="100vh" bg="gray.100">
      <BrowserCompatibilityWarning />
      <ErrorBoundary>
        {error && (
          <Box color="red.600" bg="red.100" p={3} mb={4} borderRadius="md" maxW="lg" mx="auto" textAlign="center">
            {error}
          </Box>
        )}
        {currentScreen === 'landing' && <LandingPage onGetStarted={handleLandingGetStarted} />}
        {currentScreen === 'setup' && <MatchSetup onComplete={handleSetupComplete} onBackToLanding={handleBackToLanding} />}
        {currentScreen === 'join' && <JoinMatch onJoin={handleJoinMatch} />}
        {currentScreen === 'scoring' && matchData && matchData.scoringStructure !== 'tennis' && (
          <ScoringScreen
            matchId={matchData.id}
            team1Color={matchData.team1Color}
            team2Color={matchData.team2Color}
            isController={isController}
            team1Name={matchData.team1Name}
            team2Name={matchData.team2Name}
            showQRCode={matchData.showQRCode}
            courtNumber={matchData.courtNumber}
          />
        )}
        {currentScreen === 'tennis' && matchData && matchData.scoringStructure === 'tennis' && (
          <TennisScoringScreen
            matchId={matchData.id}
            team1Color={matchData.team1Color}
            team2Color={matchData.team2Color}
            isController={isController}
            team1Name={matchData.team1Name}
            team2Name={matchData.team2Name}
            showQRCode={matchData.showQRCode}
            courtNumber={matchData.courtNumber}
          />
        )}
      </ErrorBoundary>
    </Box>
  )
}

// Landing page component with navigation
function LandingPageRoute() {
  const navigate = useNavigate()
  const location = useLocation()
  
  // Handle matchId in URL for landing page (deployed version logic)
  useEffect(() => {
    if (location.pathname === '/') {
      const searchParams = new URLSearchParams(location.search)
      const matchId = searchParams.get('matchId')
      
      if (matchId) {
        getMatch(matchId).then(matchData => {
          if (matchData) {
            const isTennis = matchData.scoringStructure === 'tennis'
            navigate(`/${isTennis ? 'tennis' : 'scoring'}${location.search}`, { replace: true })
          }
        })
      }
    }
  }, [location, navigate])
  
  return <LandingPage onGetStarted={() => navigate('/match-setup')} />
}

// Match setup component with navigation
function MatchSetupRoute() {
  const navigate = useNavigate()
  const handleSetupComplete = (data: MatchData) => {
    const isTennis = data.scoringStructure === 'tennis'
    navigate(`/${isTennis ? 'tennis' : 'scoring'}?matchId=${data.id}&token=${data.controllerToken}`)
  }
  
  return <MatchSetup onComplete={handleSetupComplete} onBackToLanding={() => navigate('/')} />
}

// Auth redirect component
function AuthRedirect() {
  const navigate = useNavigate()
  const { user } = useAuthContext()
  
  useEffect(() => {
    if (user) {
      // Redirect to landing page after successful authentication
      navigate('/', { replace: true })
    }
  }, [user, navigate])
  
  return <AuthPage />
}

// Root App component
export function App() {
  return (
    <AuthProvider>
      <PaywallProvider>
        <PaymentAlertToast />
        <Routes>
          <Route path="/" element={
            <PaywallGuard requireAuth={false} requireSubscription={false}>
              <LandingPageRoute />
            </PaywallGuard>
          } />
          <Route path="/scoring" element={
            <ScoringAccessGuard>
              <TrialBlockingGuard>
                <MatchLoader />
              </TrialBlockingGuard>
            </ScoringAccessGuard>
          } />
          <Route path="/tennis" element={
            <ScoringAccessGuard>
              <TrialBlockingGuard>
                <MatchLoader />
              </TrialBlockingGuard>
            </ScoringAccessGuard>
          } />
          <Route path="/pricing" element={
            <PaywallGuard requireAuth={false} requireSubscription={false}>
              <Pricing />
            </PaywallGuard>
          } />
          <Route path="/register" element={<AuthPage defaultMode="register" />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/auth" element={<AuthRedirect />} />
          <Route path="/verify-code" element={<VerifyCode />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/paywall" element={<PaywallPage />} />

          <Route path="/match-setup" element={
            <EmailVerificationGuard>
              <PaywallGuard requireAuth={false} requireSubscription={false}>
                <MatchSetupRoute />
              </PaywallGuard>
            </EmailVerificationGuard>
          } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <EmailVerificationGuard>
              <PaywallGuard requireAuth={true} requireSubscription={false}>
                <ProfilePage />
              </PaywallGuard>
            </EmailVerificationGuard>
          </ProtectedRoute>
        } />
        <Route path="*" element={
          <PaywallGuard requireAuth={false} requireSubscription={false}>
            <AppContent />
          </PaywallGuard>
        } />
        </Routes>
      </PaywallProvider>
    </AuthProvider>
  )
} 